/**
 * "Go to a world" (2D): a panel to pick another world and travel there, and
 * to invite people in this room along. Plus the invitee's prompt
 * ("<Name> invited you to <World> — Go / Not now") and the inviter's toast.
 *
 * <WorldHopLayer> mounts once per viewer page: it listens for invites on the
 * room channel (also while in VR, where VRWorldHop shows the prompt instead).
 */
import React, { useEffect, useState } from 'react';
import { ArrowLeft, Check, Globe, Loader, Lock, Search, Send, Users, X } from 'lucide-react';
import { getCurrentChannel, getCurrentWorldId } from '../../services/realtimeManager';
import {
  HOP_SECTIONS, INVITE_EVENT, INVITE_PROMPT_MS, INVITE_REPLY_EVENT,
  acceptInvite, declineInvite, receiveInvite, receiveReply, useWorldHop, type HopWorld, type IncomingInvite,
} from '../../services/worldHop';
import { useWorldHopPicker } from './useWorldHopPicker';
import './WorldHop.css';

/** Listen for invites and replies on this world's room channel. */
export function useInviteListener(worldId: string | undefined, enabled: boolean) {
  useEffect(() => {
    if (!worldId || !enabled) return;
    let live = true;
    // The room channel is shared (MultiplayerSync subscribes it) and gets
    // REPLACED when MultiplayerSync remounts (e.g. after choosing avatar or
    // view-only), which drops handlers bound to the old one. So bind to
    // whichever channel is current, and re-bind when it changes. Broadcast
    // handlers work whether added before or after the channel joins.
    const bound = new WeakSet<object>();
    const ensure = () => {
      const ch = getCurrentChannel();
      if (!live || !ch || getCurrentWorldId() !== worldId || bound.has(ch)) return;
      bound.add(ch);
      ch.on('broadcast', { event: INVITE_EVENT }, (m: any) => { if (live) void receiveInvite(m?.payload); });
      ch.on('broadcast', { event: INVITE_REPLY_EVENT }, (m: any) => { if (live) receiveReply(m?.payload); });
    };
    ensure();
    const t = setInterval(ensure, 1000);
    return () => { live = false; clearInterval(t); };
  }, [worldId, enabled]);

  // Old invites fade out of the prompt.
  const invites = useWorldHop((s) => s.invites);
  useEffect(() => {
    if (!invites.length) return;
    const oldest = Math.min(...invites.map((i) => i.at));
    const t = setTimeout(() => {
      const now = Date.now();
      for (const i of useWorldHop.getState().invites) if (now - i.at >= INVITE_PROMPT_MS) useWorldHop.getState().dropInvite(i.key);
    }, Math.max(500, oldest + INVITE_PROMPT_MS - Date.now()));
    return () => clearTimeout(t);
  }, [invites]);
}

function Thumb({ w, size = 'sm' }: { w: Pick<HopWorld, 'thumb' | 'name'>; size?: 'sm' | 'lg' }) {
  const [failed, setFailed] = useState(false);
  return <div className={`wh-thumb wh-thumb--${size}`}>
    {w.thumb && !failed
      ? <img src={w.thumb} alt="" loading="lazy" onError={() => setFailed(true)} />
      : <img src="/gathrxr-hero.webp" alt="" className="wh-thumb-fallback" />}
  </div>;
}

export function WorldHopPanel({ worldId }: { worldId?: string }) {
  const open = useWorldHop((s) => s.pickerOpen);
  const close = () => useWorldHop.getState().setPickerOpen(false);
  const p = useWorldHopPicker(worldId, open);
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') close(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);
  if (!open) return null;
  const stop = (e: React.SyntheticEvent) => e.stopPropagation();
  const sel = p.selected;

  return <div className="wh-scrim" onClick={close} onPointerDown={stop}>
    <section className="wh-panel" role="dialog" aria-modal="true" aria-label="Go to a world"
      onClick={stop} onPointerDown={stop} onKeyDown={stop} onKeyUp={stop}>
      <header className="wh-head">
        {sel
          ? <button className="wh-icon-btn" onClick={() => p.choose(null)} aria-label="Back to worlds"><ArrowLeft size={18} /></button>
          : <span className="wh-head-icon"><Globe size={18} /></span>}
        <h2>{sel ? sel.name : 'Go to a world'}</h2>
        <button className="wh-icon-btn" onClick={close} aria-label="Close"><X size={18} /></button>
      </header>

      {!sel ? <>
        <label className="wh-search">
          <Search size={16} />
          <input autoFocus type="search" placeholder="Search worlds" value={p.query} onChange={(e) => p.setQuery(e.target.value)} />
        </label>
        <div className="wh-list">
          {p.worlds === null ? <p className="wh-note"><Loader size={15} className="spin" /> Loading worlds…</p>
            : p.error ? <p className="wh-note wh-note--err">{p.error}</p>
            : p.shown.length === 0 ? <p className="wh-note">{p.query ? 'No worlds match that search.' : 'No other worlds yet.'}</p>
            : HOP_SECTIONS.map((sec) => {
              const items = p.shown.filter((w) => w.section === sec.key);
              if (!items.length) return null;
              return <div key={sec.key} className="wh-section">
                <h3>{sec.label}</h3>
                <div className="wh-grid">
                  {items.map((w) => (
                    <button key={w.id} className="wh-card" onClick={() => p.choose(w)} data-world={w.id}>
                      <Thumb w={w} />
                      <span className="wh-card-name">{w.name}</span>
                      <span className="wh-card-meta">
                        {!w.isPublic && <Lock size={11} />}
                        {w.isPublic ? 'Public' : 'Private'}{w.ownerName && w.section !== 'mine' ? ` · ${w.ownerName}` : ''}
                      </span>
                    </button>
                  ))}
                </div>
              </div>;
            })}
        </div>
      </> : <div className="wh-detail">
        <div className="wh-dest">
          <Thumb w={sel} size="lg" />
          <div className="wh-dest-info">
            <span className="wh-badge">{sel.isPublic ? 'Public' : <><Lock size={11} /> Private</>}</span>
            {sel.ownerName && <span className="wh-dest-owner">by {sel.ownerName}</span>}
            <button className="wh-btn wh-btn--primary" onClick={p.go} data-wh="go">Go now</button>
          </div>
        </div>

        <div className="wh-invite">
          <div className="wh-invite-head">
            <Users size={16} /><h3>Bring people along</h3>
          </div>
          {p.inviteBlock && <p className="wh-note">{p.inviteBlock}</p>}
          {!p.inviteBlock && <>
            <ul className="wh-people">
              {p.people.map((person) => {
                const r = p.results[person.key];
                const on = p.picked.has(person.key);
                return <li key={person.key}>
                  <button className={`wh-person${on ? ' is-on' : ''}`} disabled={!!person.blockedReason || r?.ok}
                    onClick={() => p.toggle(person.key)} aria-pressed={on}>
                    <span className="wh-check">{(on || r?.ok) && <Check size={13} />}</span>
                    <span className="wh-person-name">{person.userName}</span>
                    <span className="wh-person-state">{r ? (r.ok ? 'Invited' : r.error) : person.blockedReason || ''}</span>
                  </button>
                </li>;
              })}
            </ul>
            <div className="wh-actions">
              <button className="wh-btn" disabled={p.busy} onClick={() => void p.invite(true)} data-wh="invite-all">Invite everyone here</button>
              <button className="wh-btn wh-btn--accent" disabled={p.busy || p.picked.size === 0} onClick={() => void p.invite(false)} data-wh="invite">
                {p.busy ? <Loader size={15} className="spin" /> : <Send size={15} />} Invite{p.picked.size ? ` (${p.picked.size})` : ''}
              </button>
            </div>
          </>}
          {p.status && <p className="wh-status" role="status">{p.status}</p>}
          <p className="wh-hint">Invites go to people in this world right now. They choose whether to come.{!sel.isPublic && sel.canShare ? ' Accepting gives them visitor access to this private world.' : ''}</p>
        </div>
      </div>}
    </section>
  </div>;
}

/** The invitee's prompt (2D). */
export function WorldInvitePrompt() {
  const invite = useWorldHop((s) => s.invites[0]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  useEffect(() => { setErr(null); setBusy(false); }, [invite?.key]);
  if (!invite && !err) return null;
  const accept = async (inv: IncomingInvite) => {
    setBusy(true);
    const e = await acceptInvite(inv);
    if (e) { setErr(e); setBusy(false); setTimeout(() => setErr(null), 3500); }
  };
  return <div className="wh-prompt" role="alertdialog" aria-live="assertive" aria-label="World invite"
    onPointerDown={(e) => e.stopPropagation()}>
    <span className="wh-prompt-icon"><Globe size={18} /></span>
    {invite ? <>
      <p><strong>{invite.fromName}</strong> invited you to <strong>{invite.worldName}</strong></p>
      <div className="wh-prompt-actions">
        <button className="wh-btn wh-btn--primary" disabled={busy} onClick={() => void accept(invite)} data-wh="accept">
          {busy ? <Loader size={15} className="spin" /> : null} Go
        </button>
        <button className="wh-btn" disabled={busy} onClick={() => declineInvite(invite)} data-wh="decline">Not now</button>
      </div>
    </> : <p>{err}</p>}
  </div>;
}

export function WorldHopToast() {
  const toast = useWorldHop((s) => s.toast);
  if (!toast) return null;
  return <div className="wh-toast" role="status">{toast}</div>;
}

/** Everything 2D, mounted once in the viewer. `listen`: the room channel is live. */
export function WorldHopLayer({ worldId, listen, inVR }: { worldId?: string; listen: boolean; inVR: boolean }) {
  useInviteListener(worldId, listen);
  if (inVR) return null; // VRWorldHop draws the panel and prompt in the headset
  return <>
    <WorldHopPanel worldId={worldId} />
    <WorldInvitePrompt />
    <WorldHopToast />
  </>;
}
